/* Executive summary widget — sits above the Admin tab navigation.
 * 4 big-number cards: YTD Revenue / YTD Net Income / Cash on Hand /
 * Active Subscribers.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  TrendingUp, Coins, Wallet, Users, RefreshCw, Loader2,
} from 'lucide-react';
import MatureAudiencesAcceptancesRow from './MatureAudiencesAcceptancesRow';   // Feb-2026
import { BACKEND_URL } from '@/lib/api';

const API = `${BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });

function fmtUSD(v) {
  if (v == null) return '—';
  const sign = v < 0 ? '−' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function Card({ icon: Icon, label, value, sub, tone = 'default', testid }) {
  const tones = {
    default: 'border-border bg-card',
    success: 'border-success/40 bg-success/5',
    warning: 'border-warning/40 bg-warning/5',
    primary: 'border-primary/40 bg-primary/5',
  };
  return (
    <div data-testid={testid}
          className={`rounded-md border p-3 ${tones[tone] || tones.default}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
        <Icon className="h-3.5 w-3.5 text-primary opacity-70" />
      </div>
      <div className="mt-1 truncate text-2xl font-semibold tracking-tight font-mono-stat">
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[10.5px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function ExecSummary({ compact = false, drawer = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get('/admin/exec-summary');
      setData(r.data);
    } catch (e) { console.debug('[exec-summary] fallback to em-dash:', e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener('finance:updated', refresh);
    return () => window.removeEventListener('finance:updated', refresh);
  }, []);

  // Feb-2026 · Compact variant — a small square that fits in the
  // Finance sidebar next to the AI advisor. Two-by-two mini stats,
  // no acceptances row, tight padding.
  if (compact) {
    return (
      <div className="rounded-md border border-border bg-card p-2.5" data-testid="admin-exec-summary-compact">
        <div className="flex items-center justify-between">
          <div className="text-[9.5px] uppercase tracking-[0.18em] text-muted-foreground">
            Exec · YTD {data?.year ?? new Date().getUTCFullYear()}
          </div>
          <button onClick={load} disabled={loading} data-testid="admin-exec-refresh-compact"
                  className="inline-flex items-center gap-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-60">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
        <div className="mt-1.5 grid grid-cols-2 gap-1.5">
          <div className="rounded-sm border border-success/40 bg-success/5 p-1.5" data-testid="admin-exec-revenue-compact">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
              <TrendingUp className="h-2.5 w-2.5" /> Revenue
            </div>
            <div className="mt-0.5 font-mono-stat text-[13px] font-semibold text-success">
              {data ? fmtUSD(data.ytd_revenue) : '—'}
            </div>
          </div>
          <div className={`rounded-sm border p-1.5 ${
            (data?.ytd_net_income ?? 0) >= 0 ? 'border-success/40 bg-success/5' : 'border-warning/40 bg-warning/5'
          }`} data-testid="admin-exec-net-income-compact">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
              <Coins className="h-2.5 w-2.5" /> Net Inc.
            </div>
            <div className="mt-0.5 font-mono-stat text-[13px] font-semibold">
              {data ? fmtUSD(data.ytd_net_income) : '—'}
            </div>
          </div>
          <div className="rounded-sm border border-primary/40 bg-primary/5 p-1.5" data-testid="admin-exec-cash-compact">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
              <Wallet className="h-2.5 w-2.5" /> Cash
            </div>
            <div className="mt-0.5 font-mono-stat text-[13px] font-semibold text-primary">
              {data ? fmtUSD(data.cash_on_hand) : '—'}
            </div>
          </div>
          <div className="rounded-sm border border-border bg-panel-header/40 p-1.5" data-testid="admin-exec-subs-compact">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-muted-foreground">
              <Users className="h-2.5 w-2.5" /> Subs
            </div>
            <div className="mt-0.5 font-mono-stat text-[13px] font-semibold">
              {data ? (data.active_subscribers.total ?? 0).toLocaleString() : '—'}
            </div>
          </div>
        </div>
        <div className="mt-1.5 grid grid-cols-3 gap-1 border-t border-border pt-1.5 text-center" data-testid="admin-exec-balance-compact">
          <div><div className="text-[8px] uppercase text-muted-foreground">Assets</div><div className="font-mono-stat text-[10px]" data-testid="admin-exec-assets-compact">{data ? fmtUSD(data.total_assets) : '—'}</div></div>
          <div><div className="text-[8px] uppercase text-muted-foreground">Liabilities</div><div className="font-mono-stat text-[10px]" data-testid="admin-exec-liabilities-compact">{data ? fmtUSD(data.total_liabilities) : '—'}</div></div>
          <div><div className="text-[8px] uppercase text-muted-foreground">Equity</div><div className="font-mono-stat text-[10px]" data-testid="admin-exec-equity-compact">{data ? fmtUSD(data.total_equity) : '—'}</div></div>
        </div>
      </div>
    );
  }

  return (
    <div className={drawer ? '' : 'border-b border-border bg-panel-header/30 px-4 py-3'} data-testid="admin-exec-summary">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground">
          Executive snapshot · YTD {data?.year ?? new Date().getUTCFullYear()}
        </div>
        <button onClick={load} disabled={loading} data-testid="admin-exec-refresh"
                className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-1 text-[10.5px] hover:bg-secondary/80 disabled:opacity-60">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Reload
        </button>
      </div>
      <div className={`mt-2 grid grid-cols-2 gap-2 ${drawer ? '' : 'md:grid-cols-4'}`}>
        <Card
          icon={TrendingUp} label="YTD Revenue"
          tone={(data?.ytd_revenue ?? 0) > 0 ? 'success' : 'default'}
          value={data ? fmtUSD(data.ytd_revenue) : <Loader2 className="h-4 w-4 animate-spin" />}
          sub="The Loom + Bookstore + Maven"
          testid="admin-exec-revenue"
        />
        <Card
          icon={Coins} label="YTD Net Income"
          tone={(data?.ytd_net_income ?? 0) >= 0 ? 'success' : 'warning'}
          value={data ? fmtUSD(data.ytd_net_income) : <Loader2 className="h-4 w-4 animate-spin" />}
          sub="Revenue − COGS − OpEx"
          testid="admin-exec-net-income"
        />
        <Card
          icon={Wallet} label="Cash on Hand"
          tone="primary"
          value={data ? fmtUSD(data.cash_on_hand) : <Loader2 className="h-4 w-4 animate-spin" />}
          sub={data?.balanced ? '✓ Books balanced' : '⚠ Balance sheet drift'}
          testid="admin-exec-cash"
        />
        <Card
          icon={Users} label="Active Subscribers"
          value={data ? (data.active_subscribers.total ?? 0).toLocaleString() : <Loader2 className="h-4 w-4 animate-spin" />}
          sub={data ? `${data.active_subscribers.pro_writer} The Loom · ${data.active_subscribers.weekly_maven} Maven` : ''}
          testid="admin-exec-subscribers"
        />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 rounded-sm border border-border bg-background/60 p-2" data-testid="admin-exec-balance-sheet-summary">
        <div><div className="text-[9px] uppercase text-muted-foreground">Total assets</div><div className="font-mono-stat text-[12px] font-semibold" data-testid="admin-exec-total-assets">{data ? fmtUSD(data.total_assets) : '—'}</div></div>
        <div><div className="text-[9px] uppercase text-muted-foreground">Total liabilities</div><div className="font-mono-stat text-[12px] font-semibold" data-testid="admin-exec-total-liabilities">{data ? fmtUSD(data.total_liabilities) : '—'}</div></div>
        <div><div className="text-[9px] uppercase text-muted-foreground">Total equity</div><div className="font-mono-stat text-[12px] font-semibold" data-testid="admin-exec-total-equity">{data ? fmtUSD(data.total_equity) : '—'}</div></div>
      </div>
      {/* Feb-2026 · Mature Audiences · content-warning acceptances mini-row. */}
      <MatureAudiencesAcceptancesRow days={7} />
    </div>
  );
}
